// Step 23 — AI Contract Intelligence Workspace.
//
// Two exports:
//   AIContracts       — /deals/:workspaceId/contracts — comparison workspace
//   AIContractDetail  — /deals/:workspaceId/contracts/:contractDraftId
//                       — per-contract intelligence workspace
//
// Both pages consume ONLY existing endpoints:
//   listContracts(workspaceId)                    (Step 16)
//   getContract(workspaceId, contractDraftId)      (Step 16)
//   listDealActions(workspaceId, {acquirer_id})    (Step 13, via AIContractActions)
//
// Zero new backend endpoints, zero new domain models. Every value
// rendered is authoritative — no invented completion percentage, no
// fabricated clause coverage, no synthesized "quality score".
//
// Workspace isolation stays server-side (endpoints re-validate via
// `for_user()`). The frontend never trusts a contract id from the
// URL — the API returns 404 for contracts outside the caller's
// workspaces, and the page renders an honest empty state in that case.

import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { getContract, listContracts } from "../api";
import {
  AIEmpty,
  AIError,
  AILoading,
  AIPageHeader,
  AISection,
} from "../shell/AIPrimitives";
import { useAIWorkspace } from "../shell/AIWorkspaceContext";
import AIAskQadaButton from "../shell/intelligence/AIAskQadaButton";
import AIContractActions from "../shell/contracts/AIContractActions";
import AIContractAttentionChips from "../shell/contracts/AIContractAttentionChips";
import AIContractClauses from "../shell/contracts/AIContractClauses";
import AIContractComparisonTable from "../shell/contracts/AIContractComparisonTable";
import AIContractEvidence from "../shell/contracts/AIContractEvidence";
import AIContractFindings from "../shell/contracts/AIContractFindings";
import AIContractIdentityCard from "../shell/contracts/AIContractIdentityCard";
import AIContractStatusCard from "../shell/contracts/AIContractStatusCard";
import AIContractUncertainties from "../shell/contracts/AIContractUncertainties";
import AIContractVariables from "../shell/contracts/AIContractVariables";
import {
  buildContractAskPrompt,
  buildContractComparisonAskPrompt,
  contractRowsForComparison,
} from "../shell/contracts/helpers";


// ── /deals/:workspaceId/contracts ─────────────────────────────────

export function AIContracts() {
  const { activeWorkspaceId, activeWorkspace } = useAIWorkspace();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(
    async ({ signal } = {}) => {
      if (!activeWorkspaceId) return;
      setLoading(true);
      setError(null);
      try {
        const res = await listContracts(
          activeWorkspaceId,
          { limit: 100 },
          { signal },
        );
        setRows(res?.results || []);
      } catch (e) {
        if (e?.name === "AbortError") return;
        setError(
          e?.body?.detail || e.message || "Kunde inte ladda avtalsutkast.",
        );
      } finally {
        setLoading(false);
      }
    },
    [activeWorkspaceId],
  );

  useEffect(() => {
    const c = new AbortController();
    load({ signal: c.signal });
    return () => c.abort();
  }, [load]);

  if (!activeWorkspaceId) {
    return <AIEmpty title="Ingen affär vald" />;
  }
  if (loading && rows.length === 0) {
    return <AILoading label="Laddar avtalsutkast…" />;
  }

  const comparisonRows = contractRowsForComparison(rows);

  return (
    <>
      <AIPageHeader
        title="Avtal"
        subtitle={activeWorkspace?.organisation_name}
        actions={
          comparisonRows.length > 0 ? (
            <AIAskQadaButton
              workspaceId={activeWorkspaceId}
              prompt={buildContractComparisonAskPrompt({
                workspaceName: activeWorkspace?.organisation_name,
              })}
              label="Fråga Qada om avtalen"
            />
          ) : null
        }
      />
      <AIError message={error} />

      {comparisonRows.length === 0 ? (
        <AIEmpty
          title="Inga avtalsutkast registrerade ännu"
          hint={
            "AI-drafting skapar utkast via `generate_contract_draft` " +
            "eller en människa via granskningsvyn."
          }
        />
      ) : (
        <AISection
          title="Jämförelse"
          right={
            <span className="qai-page-section-hint">
              {comparisonRows.length} utkast · deterministisk data
            </span>
          }
        >
          <AIContractComparisonTable
            rows={comparisonRows}
            workspaceId={activeWorkspaceId}
          />
        </AISection>
      )}
    </>
  );
}


// ── /deals/:workspaceId/contracts/:contractDraftId ────────────────

export function AIContractDetail() {
  const { contractDraftId } = useParams();
  const { activeWorkspaceId, activeWorkspace } = useAIWorkspace();
  const navigate = useNavigate();

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!activeWorkspaceId || !contractDraftId) return;
    const c = new AbortController();
    (async () => {
      setLoading(true);
      setError(null);
      try {
        // Single-shot read — the backend validates workspace ownership
        // via `for_user()` before returning any field. A cross-workspace
        // id returns 404, and we render the honest empty state below.
        const d = await getContract(activeWorkspaceId, contractDraftId, {
          signal: c.signal,
        });
        setDetail(d);
      } catch (e) {
        if (e?.name === "AbortError") return;
        setError(
          e?.body?.detail || e.message || "Kunde inte ladda avtalsutkastet.",
        );
      } finally {
        setLoading(false);
      }
    })();
    return () => c.abort();
  }, [activeWorkspaceId, contractDraftId]);

  if (loading && !detail) return <AILoading label="Laddar avtalsutkast…" />;

  if (!detail) {
    return (
      <>
        <AIPageHeader
          title={`Avtal #${contractDraftId}`}
          actions={
            <button
              type="button"
              className="qai-da-btn"
              onClick={() => navigate(`/deals/${activeWorkspaceId}/contracts`)}
            >
              ← Alla avtal
            </button>
          }
        />
        <AIError message={error} />
        <AIEmpty title="Ingen data tillgänglig för det här avtalet." />
      </>
    );
  }

  return (
    <>
      <AIPageHeader
        title={detail.title || `Avtal #${contractDraftId}`}
        subtitle={activeWorkspace?.organisation_name}
        actions={
          <div style={{ display: "flex", gap: 8 }}>
            <AIAskQadaButton
              workspaceId={activeWorkspaceId}
              prompt={buildContractAskPrompt({
                contractId: detail.id,
                contractTitle: detail.title,
                contractKind: detail.kind,
                workspaceName: activeWorkspace?.organisation_name,
              })}
              label="Fråga Qada om detta avtal"
            />
            <button
              type="button"
              className="qai-da-btn"
              onClick={() => navigate(`/deals/${activeWorkspaceId}/contracts`)}
            >
              ← Alla avtal
            </button>
          </div>
        }
      />
      <AIError message={error} />

      {/* Attention chips — deterministic observations only. */}
      <div className="qai-contract-attention-row">
        <AIContractAttentionChips row={detail} />
      </div>

      <div className="qai-contract-detail-grid">
        <AIContractIdentityCard detail={detail} />
        <AIContractStatusCard detail={detail} />
      </div>

      <AIContractVariables detail={detail} />
      <AIContractUncertainties detail={detail} />
      <AIContractClauses detail={detail} />
      <AIContractEvidence detail={detail} />

      <AIContractFindings detail={detail} workspaceId={activeWorkspaceId} />

      <AIContractActions detail={detail} workspaceId={activeWorkspaceId} />

      <div className="qai-shell-empty-hint" style={{ marginTop: 20 }}>
        Godkännande, avvisning, utskick för signering, signering, och
        supersedning sker manuellt via granskningsvyn.{" "}
        <Link
          to={`/deals/${activeWorkspaceId}/contracts/review`}
          className="qai-page-section-link"
        >
          Öppna granskningsvyn för denna affär →
        </Link>
      </div>
    </>
  );
}
